import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { runSync } from '../../sync/SyncEngine'

import Layout from '../../common/components/Layout/Layout'
import SwipeableCalendar from '../../common/components/SwipeableCalendar/SwipeableCalendar'
import { getNoRowOverlayInvitations, getNoResultsOverlayInvitations, debounce } from '../../common/utilities/utilities'

import {
  useTheme,
  Stack,
  TextField,
  InputAdornment,
  Alert,
  Button,
  Tooltip,
} from '@mui/material'
import {
  Search as SearchIcon,
  Event as EventIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { subMonths, addMonths, isAfter, isBefore, format } from 'date-fns'
import { areDatesEqualFromJS, formatDateToISOShort, formatDateToMed, isTodayFromJS, today } from '../../common/utilities/FormatDate'
import {
  attachInvitationQrValidity,
  getInvitationTimeStatus,
} from '../../common/utilities/invitationValidations'
import * as XLSX from 'xlsx'
import db from '../../db/db'

import NewVisitor from '../NewVisitorPage/NewVisitor'
import PreRegisterDetailsDialog from './PreRegisterDetailsDialog'
import './PreRegisters.sass'

const ENTRY_VIGENCIA_TICK_MS = 30_000

/**
 * Same QR vigencia window as VisitForm: stamp company policy, then
 * `getInvitationTimeStatus`. Used to disable "Register entry" and for tooltips.
 */
function getEntryAvailability(rawInvitation, companies, now, t) {
  const stamped = attachInvitationQrValidity(rawInvitation, companies)
  const { outOfTime, isOutOfPrevTime } = getInvitationTimeStatus(stamped, now)
  if (!outOfTime) {
    return { available: true, tooltip: '' }
  }
  if (isOutOfPrevTime) {
    const prev = stamped.allowed_invitation_qr_validity_prev_time || 0
    return { available: false, tooltip: t('Early QR code', { x: prev }) }
  }
  return { available: false, tooltip: t('QR expired description') }
}

// Extrae fecha/hora de una invitación. Tanto la respuesta en vivo de
// `GET /invitations/batch` como la copia cacheada en Dexie comparten el mismo
// shape del backend: `invitation_date` ('YYYY-MM-DD') + `invitation_time`
// (datetime). Se mantienen los fallbacks anteriores por si llega otro shape.
//
// La hora se toma en la zona embebida del ISO (`setZone: true`), no con
// `Date#getHours()` del navegador — si el browser está en Caracas (UTC−4) y
// la invitación en −06:00, `new Date(...-06:00).getHours()` mostraría 10:00
// para un ingreso programado a las 08:00.
function extractDateAndTime(inv) {
  if (inv.invitation_date) {
    const day = String(inv.invitation_date).split('T')[0]
    let time = ''
    const rawTime = inv.invitation_datetime || inv.invitation_time
    if (rawTime) {
      const parsed = DateTime.fromISO(String(rawTime), { setZone: true })
      if (parsed.isValid) {
        time = parsed.toFormat('HH:mm')
      }
    }
    return { day, time }
  }

  const entryDate = inv.date || inv.entry_time || inv.scheduled_date || ''
  const dateParts = entryDate ? entryDate.split('T') : ['', '']
  return { day: dateParts[0] || '', time: dateParts[1] ? dateParts[1].substring(0, 5) : '' }
}

// Normaliza una invitación (online o de IndexedDB) al formato que espera el DataGrid
function normalizeInvitation(inv, index) {
  const { day, time } = extractDateAndTime(inv)
  const firstName = inv.first_name || ''
  const lastName = inv.last_name_one || inv.last_name || ''
  const lastName2 = inv.last_name_two || ''
  const fullName = `${firstName} ${lastName} ${lastName2}`.trim()

  const company = inv.destination?.name || inv.company || ''
  const host = inv.user?.name || inv.host || ''
  const invitationTime = formatInvitationDateTime(day, time)

  return {
    id: inv.id ?? index,
    _uuid: inv.id ?? index,
    day, 
    entry_time: time,
    full_name: fullName,
    identification: inv.identification || '',
    comes_from: inv.comes_from || '',
    company,
    host,
    notes: inv.notes || '',
    _raw: inv,
    invitation_time: invitationTime,
    syncStatus: inv.syncStatus || 'synced',
  }
}

// Construye el texto mostrado en la columna "Scheduled entry time" a partir
// de `day`/`time` — los MISMOS valores ya usados para filtrar y ordenar
// (ver `extractDateAndTime`) — en vez de formatear directamente el
// `invitation_time` crudo. Esto evita que la fecha visible quede
// desincronizada de la fecha real de la fila (p. ej. en invitaciones
// multi-día donde `invitation_time` puede conservar la fecha del primer
// día del rango mientras `invitation_date`/`day` avanza correctamente).
function formatInvitationDateTime(day, time) {
  if (!day) return ''

  const [year, month, dayNum] = day.split('-').map(Number)
  const [hours, minutes] = (time || '00:00').split(':').map(Number)
  const composed = new Date(year, month - 1, dayNum, hours || 0, minutes || 0)

  if (isNaN(composed.getTime())) return ''

  return `${format(composed, 'dd-MMM-yy')}  ${format(composed, 'h:mma').toLowerCase()}`
}

function SyncStatusIcon({ status }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const iconSx = { fontSize: 24, display: 'block' }

  if (status === 'pending') {
    return (
      <Tooltip title={t('sync_pending')}>
        <Warning sx={{ ...iconSx, color: '#CB7B01' }} />
      </Tooltip>
    )
  }

  if (status === 'failed') {
    return (
      <Tooltip title={t('sync_failed')}>
        <ErrorIcon sx={{ ...iconSx, color: theme.palette.error.main }} />
      </Tooltip>
    )
  }

  return (
    <Tooltip title={t('sync_done')}>
      <CheckCircle sx={{ ...iconSx, color: '#128724' }} />
    </Tooltip>
  )
}

const PreRegisters = props => {
  const {
    ask_visit_identification = false,
    companies = [],
    custom_visit_identification_label = '',
    domain,
    enabled_lobby_preregister = true,
    isOnline = true,
  } = props

  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const identificationLabel = custom_visit_identification_label || t('Identification')
  const canCreatePreRegister = enabled_lobby_preregister !== false

  const [date, setDate] = useState([today, today])
  const [openCalendar, setOpenCalendar] = useState(false)
  const [calendarDate, setCalendarDate] = useState(date)
  const [allInvitations, setAllInvitations] = useState([])
  const [prefix, setPrefix] = useState('')
  const [searching, setSearching] = useState(false)
  const [disabledOnContinue, setDisabledOnContinue] = useState(false)
  const [calendarAlertMessage, setCalendarAlertMessage] = useState('')
  const [showCreatePreRegister, setShowCreatePreRegister] = useState(false)
  const [pageSize, setPageSize] = useState(15)
  const [now, setNow] = useState(() => new Date())
  const [selectedInvitation, setSelectedInvitation] = useState(null)
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false)

  // Re-evalúa vigencia del botón «Registrar ingreso» sin salir de la pantalla
  // (early → válido → vencido). 30s alinea con el poll de sync.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), ENTRY_VIGENCIA_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  // Lectura siempre local en IndexedDB, sin importar `isOnline`: el
  // calendario de esta pantalla limita la selección a ±3 meses (ver más
  // abajo) y la ventana de sincronización local de invitaciones es
  // exactamente `SYNC_WINDOW_DAYS` (90 días, ver db.js), así que `db.invitations`
  // ya contiene todo lo que el usuario puede llegar a pedir aquí — igual
  // patrón que `Visits.js` (loadVisitsFromDB). Evita el `GET /invitations/batch`
  // bloqueante que antes se disparaba en cada entrada a la pantalla.
  const loadInvitationsFromDB = useCallback(async () => {
    try {
      const raw = await db.invitations.toArray()
      const visible = raw.filter(invitation => !invitation._delete)
      const normalized = visible.map((inv, i) => normalizeInvitation(inv, i))
      setAllInvitations(normalized)
    } catch (err) {
      console.error('[PreRegisters] Error cargando invitaciones offline:', err)
    }
  }, [])

  useEffect(() => {
    loadInvitationsFromDB()
  }, [loadInvitationsFromDB])

  // Recarga cuando el sync trae datos nuevos o cuando una visita pendiente
  // se escribe en Dexie — sin distinguir online/offline, igual que `Visits.js`.
  useEffect(() => {
    const handler = () => loadInvitationsFromDB()
    window.addEventListener('lobby:sync-complete', handler)
    window.addEventListener('lobby:sync-error', handler)
    window.addEventListener('lobby:visit-pending', handler)
    window.addEventListener('lobby:push-applied', handler)
    return () => {
      window.removeEventListener('lobby:sync-complete', handler)
      window.removeEventListener('lobby:sync-error', handler)
      window.removeEventListener('lobby:visit-pending', handler)
      window.removeEventListener('lobby:push-applied', handler)
    }
  }, [loadInvitationsFromDB])

  // Autocura la carrera de arranque en frío: `LoadPage` navega a la app en
  // cuanto termina SU propio sync acotado ("hoy"), sin esperar a que el
  // `runSync()` paralelo de `useSyncEngine` traiga la ventana histórica
  // completa de invitaciones (±90 días, checkpoint `invitations_window`, ver
  // SyncEngine.js). Si el usuario llega a esta pantalla antes de que ese
  // `runSync()` termine (o si falló silenciosamente en una sesión previa),
  // esta pantalla dispara uno propio. `runSync()` ya es seguro de llamar en
  // paralelo (guard `isSyncing` interno) — si ya hay uno en curso, este
  // simplemente no hace nada.
  useEffect(() => {
    if (!isOnline) return
    let cancelled = false

    db.sync_meta.get('invitations_window').then(meta => {
      if (!cancelled && !meta) {
        runSync().catch(err => console.error('[PreRegisters] Error sincronizando ventana de invitaciones:', err))
      }
    })

    return () => { cancelled = true }
  }, [isOnline])

  // `runPoll()` (cada ~60 s) no emite `lobby:sync-start`, así que este estado
  // solo se activa durante un `runSync()` completo (cold start, reconexión,
  // o el de arriba) — evita que el DataGrid parpadee un loading en cada
  // poll rutinario en segundo plano.
  const [syncing, setSyncing] = useState(false)
  useEffect(() => {
    const handleStart = () => setSyncing(true)
    const handleEnd = () => setSyncing(false)
    window.addEventListener('lobby:sync-start', handleStart)
    window.addEventListener('lobby:sync-complete', handleEnd)
    window.addEventListener('lobby:sync-error', handleEnd)
    return () => {
      window.removeEventListener('lobby:sync-start', handleStart)
      window.removeEventListener('lobby:sync-complete', handleEnd)
      window.removeEventListener('lobby:sync-error', handleEnd)
    }
  }, [])

  const filteredInvitations = useMemo(() => {
    const [start, end] = date
    // Comparamos como strings 'YYYY-MM-DD' (mismo formato que `inv.day`) en vez
    // de construir objetos Date: evita el desfase de timezone de interpretar
    // una fecha-sin-hora como medianoche UTC contra un `startOfDay`/`endOfDay`
    // local, y falla "cerrado" (excluye) cuando la fecha no se puede determinar,
    // en vez de mostrar filas fuera de rango.
    const startStr = formatDateToISOShort(start)
    const endStr = formatDateToISOShort(end)

    return allInvitations
      .filter(inv => {
        if (!inv.day || inv.day < startStr || inv.day > endStr) return false

        if (prefix) {
          const lower = prefix.toLowerCase()
          const searchFields = [
            inv.full_name,
            inv.identification,
            inv.comes_from,
            inv.company,
            inv.host,
          ].join(' ').toLowerCase()
          if (!searchFields.includes(lower)) return false
        }

        return true
      })
      // Orden fijo tipo "cola" por fecha/hora de la cita (ascendente, la más
      // próxima primero) — `day` ('YYYY-MM-DD') y `entry_time` ('HH:mm') ya
      // son strings zero-padded, comparables lexicográficamente sin construir
      // objetos Date. No hay sort interactivo por columna (ver `columns`,
      // todas con `sortable: false`).
      .sort((a, b) => {
        if (a.day !== b.day) return a.day < b.day ? -1 : 1
        return (a.entry_time || '').localeCompare(b.entry_time || '')
      })
  }, [allInvitations, date, prefix])

  useEffect(() => {
    const minAllowedDate = subMonths(today, 3)
    const maxAllowedDate = addMonths(today, 3)

    if (isBefore(calendarDate[0], minAllowedDate)) {
      setDisabledOnContinue(true)
      setCalendarAlertMessage(t('Calendar alert past'))
    } else if (isAfter(calendarDate[1], maxAllowedDate)) {
      setDisabledOnContinue(true)
      setCalendarAlertMessage(t('Calendar alert future'))
    } else {
      setDisabledOnContinue(false)
      setCalendarAlertMessage('')
    }
  }, [calendarDate, t])

  const onEfficientSearch = useMemo(
    () => debounce(value => setPrefix(value), 500),
    []
  )

  const onChangePrefix = value => {
    value ? setSearching(true) : setSearching(false)
    onEfficientSearch(value)
  }

  const getFilterDate = () => {
    const [start, end] = date
    if (areDatesEqualFromJS(start, end)) {
      return `${isTodayFromJS(start) ? t('today') : formatDateToMed(start)}`
    }
    return `${isTodayFromJS(start) ? t('today') : formatDateToMed(start)} - ${isTodayFromJS(end) ? t('today') : formatDateToMed(end)}`
  }

  const onContinue = () => {
    let [startDate, endDate] = calendarDate
    if (!startDate && !endDate) {
      startDate = today
      endDate = today
    } else if (!startDate) {
      startDate = endDate
    } else if (!endDate) {
      endDate = startDate
    }
    setDate([startDate, endDate])
    setOpenCalendar(false)
  }

  const onCloseCalendar = useCallback(() => {
    setOpenCalendar(false)
    setCalendarDate(date)
  }, [date])

  const renderCalendarAlert = useCallback(() => {
    if (disabledOnContinue && calendarAlertMessage) {
      return (
        <Alert severity='error' icon={false} style={{ color: '#CD2A26' }}>
          {calendarAlertMessage}
        </Alert>
      )
    }
    return null
  }, [disabledOnContinue, calendarAlertMessage])

  const openVisitFormFromInvitation = useCallback(rawInvitation => {
    navigate('/', { state: { fromPreRegister: rawInvitation } })
  }, [navigate])

  const openInvitationDetails = useCallback(rawInvitation => {
    setOpenDeleteConfirmation(false)
    setSelectedInvitation(rawInvitation)
  }, [])

  const openInvitationDeleteConfirmation = useCallback(rawInvitation => {
    setOpenDeleteConfirmation(true)
    setSelectedInvitation(rawInvitation)
  }, [])

  const columns = useMemo(() => [
    {
      sortable: false,
      headerName: t('Scheduled entry time'),
      field: 'invitation_time',
      width: 200,
    },
    { sortable: false, headerName: t('name'), field: 'full_name', width: 400 },
    ...(ask_visit_identification
      ? [{ sortable: false, headerName: identificationLabel, field: 'identification', width: 200 }]
      : []),
    { sortable: false, headerName: t('Comes from'), field: 'comes_from', width: 200 },
    { sortable: false, headerName: t('Go to'), field: 'company', width: 200 },
    { sortable: false, headerName: t('host'), field: 'host', width: 200 },
    { sortable: false, headerName: t('preregistration_reception_notes'), field: 'notes', width: 200 },
    {
      sortable: false,
      headerName: t('sync_status'),
      field: 'syncStatus',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => <SyncStatusIcon status={value} />,
    },
    {
      sortable: false,
      headerName: '',
      field: 'register_entry',
      width: 180,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'register-entry-cell',
      renderCell: ({ row }) => {
        const { available, tooltip } = getEntryAvailability(row?._raw, companies, now, t)
        const disabled =
          !available ||
          !row?._raw?.destination?.id ||
          row?.day !== formatDateToISOShort(today)

        const button = (
          <Button
            variant='contained'
            size='small'
            sx={{
              gap: 1
            }}
            disabled={disabled}
            onClick={() => openVisitFormFromInvitation(row._raw)}
          >
            {t('Register entry')}
          </Button>
        )

        if (!tooltip) return button

        return (
          <Tooltip title={tooltip}>
            <span>{button}</span>
          </Tooltip>
        )
      }
    },
    {
      sortable: false,
      headerName: '',
      field: 'row_actions',
      width: 260,
      align: 'center',
      headerAlign: 'center',
      cellClassName: 'pre-register-row-actions-cell',
      renderCell: ({ row }) => (
        <Stack
          className='pre-register-row-actions'
          onMouseDown={event => event.stopPropagation()}
          direction='row'
          alignItems='center'
          justifyContent='center'
          spacing={1}
        >
          <Button
            type='button'
            variant='text'
            size='small'
            onClick={event => {
              event.stopPropagation()
              openInvitationDetails(row._raw)
            }}
          >
            {t('View details')}
          </Button>
          <Button
            type='button'
            variant='text'
            size='small'
            onClick={event => {
              event.stopPropagation()
              openInvitationDeleteConfirmation(row._raw)
            }}
          >
            {t('remove')}
          </Button>
        </Stack>
      )
    },
  ], [ask_visit_identification, companies, identificationLabel, now, openInvitationDeleteConfirmation, openInvitationDetails, openVisitFormFromInvitation, t])

  const handleClickDownload = useCallback(() => {
    const headers = columns.map(col => col.headerName)
    const body = filteredInvitations.map(row => [
      `${row.day} ${row.entry_time}`,
      row.full_name,
      ...(ask_visit_identification ? [row.identification] : []),
      row.comes_from,
      row.company,
      row.host,
      row.notes,
      row.syncStatus,
    ])

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...body])
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'pre registro de visitantes')

    const startDate = formatDateToISOShort(date[0] || today)
    const endDate = formatDateToISOShort(date[1] || today)
    const dateRange = startDate === endDate ? startDate : `${startDate}_${endDate}`
    XLSX.writeFile(workbook, `${domain}_preregistros_visitantes_${dateRange}.xlsx`)
  }, [columns, filteredInvitations, ask_visit_identification, date, domain])

  const labelDisplayedRows = useCallback(
    ({ from, to, count }) =>
      `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`,
    [t]
  )

  if (showCreatePreRegister && canCreatePreRegister) {
    return (
      <NewVisitor
        onCancel={() => setShowCreatePreRegister(false)}
        onCreated={() => {
          loadInvitationsFromDB()
          setShowCreatePreRegister(false)
        }}
      />
    )
  }

  return (
    <Layout className='pre-registers'>
      <Stack direction='column' spacing={2} padding='28px 60px'>
        <Stack direction='column' spacing={2}>
          <h1>{t('Visitors Pre-registration')}</h1>
          <Stack
            direction='row'
            gap={2}
            alignItems='flex-end'
            flexWrap='wrap'
            width='100%'
            className='pre-registers-filters'
          >
            <TextField
              placeholder={t('Search')}
              onChange={e => onChangePrefix(e.target.value)}
              InputProps={{
                style: { height: '36px', fontSize: '13px' },
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack
              className='date-picker'
              flexWrap
            >
              <label>{`${t('Visit Date')}:`}</label>
              <TextField
                value={getFilterDate()}
                onClick={() => setOpenCalendar(true)}
                fullWidth
                hiddenLabel
                autoComplete='off'
                size='small'
                InputProps={{
                  style: { height: '36px', fontSize: '13px' },
                  endAdornment: (
                    <InputAdornment position='end'>
                      <EventIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <SwipeableCalendar
                type='range'
                open={openCalendar}
                date={calendarDate}
                setDate={setCalendarDate}
                setOpen={setOpenCalendar}
                onClose={onCloseCalendar}
                onChange={d => setCalendarDate(d)}
                onContinue={onContinue}
                renderMessage={renderCalendarAlert}
                clearable
                isDisabled={disabledOnContinue}
              />
            </Stack>
            <Stack direction='row' spacing={1} className='pre-registers-actions'>
              <Tooltip title={t('Download')}>
                <span>
                  <Button
                    color='primary'
                    variant='outlined'
                    disabled={!filteredInvitations.length}
                    onClick={handleClickDownload}
                    sx={{ padding: 1, minWidth: 'auto', height: 36, width: 36 }}
                  >
                    <FileDownloadIcon sx={{ height: 18, width: 18 }} />
                  </Button>
                </span>
              </Tooltip>
              {canCreatePreRegister && (
                <Button
                  variant='contained'
                  onClick={() => setShowCreatePreRegister(true)}
                >
                  {t('Guest pre-register')}
                </Button>
              )}
            </Stack>
          </Stack>
          <div className='table-wrapper'>
            <DataGrid
              className='pre-registers-data-grid'
              rows={filteredInvitations}
              columns={columns}
              headerHeight={40}
              loading={syncing && allInvitations.length === 0}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              rowsPerPageOptions={[15, 50, 100]}
              components={{
                NoRowsOverlay: () => getNoRowOverlayInvitations(theme, t),
                NoResultsOverlay: () => searching && getNoResultsOverlayInvitations(theme, t),
              }}
              componentsProps={{
                pagination: {
                  labelRowsPerPage: t('rows per page:'),
                  labelDisplayedRows,
                },
              }}
              pagination
              disableSelectionOnClick
              disableColumnMenu
              density='standard'
            />
          </div>
          <PreRegisterDetailsDialog
            open={Boolean(selectedInvitation)}
            item={selectedInvitation || {}}
            openDeleteConfirmation={openDeleteConfirmation}
            onClose={() => {
              setSelectedInvitation(null)
              setOpenDeleteConfirmation(false)
            }}
            onUpdated={loadInvitationsFromDB}
          />
        </Stack>
      </Stack>
    </Layout>
  )
}

const mapStateToProps = state => ({
  ask_visit_identification: state.profile.ask_visit_identification,
  companies: state.profile.companies,
  custom_visit_identification_label: state.profile.custom_visit_identification_label,
  domain: state.profile.domain,
  enabled_lobby_preregister: state.profile.enabled_lobby_preregister,
  isOnline: state.utilities.isOnline,
})

export default connect(mapStateToProps)(PreRegisters)
